/**
 * TextAreaField.tsx
 *
 * Unified textarea field component
 * Handles: textarea, comment-box
 */

import React from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES, DISABLED_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';

export const TextAreaField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
}) => {
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;

  const textareaMaxLength = fieldConfig.validation?.maxLength || fieldConfig.validation?.defaultMaxLength || 2000;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isPreviewMode) return;

    const newValue = e.target.value;
    // Enforce max length
    if (newValue.length <= textareaMaxLength) {
      onChange(fieldConfig.id, newValue);
    }
  };

  const textareaClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''} ${isDisabled ? DISABLED_INPUT_CLASSES : ''}`;

  const textareaElement = (
    <textarea
      id={fieldConfig.id}
      rows={4}
      value={value || ''}
      onChange={handleChange}
      onFocus={(e) => {
        if (!isPreviewMode) {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }}
      maxLength={textareaMaxLength}
      placeholder={isPreviewMode ? fieldConfig.placeholder || 'Text area' : fieldConfig.placeholder}
      disabled={isDisabled}
      className={textareaClasses}
    />
  );

  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      {textareaElement}
    </FieldWrapper>
  );
});

TextAreaField.displayName = 'TextAreaField';
